import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { hasRangePricedItems } from '../../utils/pricingUtils';

const RequestBuilderContext = createContext(null);

const normalizeItem = (item) => {
  const type = item?.type?.toString().toLowerCase() === 'product' ? 'product' : 'service';
  const stock = Number(item?.currentStock);

  return {
    id: item?.id,
    name: item?.name || 'Unnamed item',
    description: item?.description || '',
    imageUrl: item?.imageUrl,
    category: item?.category,
    type,
    price: Number(item?.price) || 0,
    pricingMode: item?.pricingMode || 'fixed',
    minimumPrice: item?.minimumPrice != null ? Number(item.minimumPrice) : null,
    maximumPrice: item?.maximumPrice != null ? Number(item.maximumPrice) : null,
    currentStock: Number.isFinite(stock) ? stock : null,
    quantity: 1,
  };
};

export const RequestBuilderProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const addItem = useCallback((item) => {
    if (!item?.id) return;
    setItems((current) => (
      current.some((entry) => entry.id === item.id)
        ? current
        : [...current, normalizeItem(item)]
    ));
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems((current) => current.filter((item) => item.id !== itemId));
  }, []);

  const updateQuantity = useCallback((itemId, nextQuantity) => {
    setItems((current) => current.map((item) => {
      if (item.id !== itemId || item.type === 'service') return item;

      const requestedQuantity = Math.max(1, Math.floor(Number(nextQuantity) || 1));
      const quantity = item.currentStock == null
        ? requestedQuantity
        : Math.min(requestedQuantity, Math.max(1, item.currentStock));

      return { ...item, quantity };
    }));
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    setIsDrawerOpen(false);
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const openForm = useCallback(() => {
    setIsDrawerOpen(false);
    setIsFormOpen(true);
  }, []);
  const closeForm = useCallback(() => setIsFormOpen(false), []);
  const hasItem = useCallback(
    (itemId) => items.some((item) => item.id === itemId),
    [items]
  );

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [items]
  );
  const hasRangePricing = useMemo(() => hasRangePricedItems(items), [items]);

  const value = useMemo(() => ({
    items,
    itemCount: items.length,
    subtotal,
    hasRangePricing,
    isDrawerOpen,
    isFormOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearAll,
    hasItem,
    openDrawer,
    closeDrawer,
    openForm,
    closeForm,
  }), [
    items,
    subtotal,
    hasRangePricing,
    isDrawerOpen,
    isFormOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearAll,
    hasItem,
    openDrawer,
    closeDrawer,
    openForm,
    closeForm,
  ]);

  return (
    <RequestBuilderContext.Provider value={value}>
      {children}
    </RequestBuilderContext.Provider>
  );
};

export const useRequestBuilder = () => {
  const context = useContext(RequestBuilderContext);
  if (!context) {
    throw new Error('useRequestBuilder must be used within a RequestBuilderProvider');
  }
  return context;
};
